import { UploadOutlined } from "@ant-design/icons"
import {
  App,
  Button,
  Checkbox,
  DatePicker,
  Drawer,
  Flex,
  Form,
  Grid,
  Tag,
  Typography,
  Upload,
  notification
} from "antd"
import type { UploadFile } from "antd"
import type { RcFile } from "antd/es/upload/interface"
import type { BaseOptionType } from "antd/lib/select"
import { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate, useParams } from "react-router"

import { HintedLabel } from "../../../components/FieldHint.tsx"
import { MODAL_CONFIRM_CONFIG } from "../../../components/Forms/FormChangedWatcher/FormChangedConfirm.tsx"
import InputNumber from "../../../components/Input/InputNumber.tsx"
import { ProjectPickerSelect } from "../../../components/Shared/ProjectPickerSelect.tsx"
import { notificationOptions } from "../../../constants/notification.config.ts"
import { RoutesPaths } from "../../../constants/routes.ts"
import { empty } from "../../../constants/placeholders.ts"
import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { useDateFormat } from "../../../hooks/use-date-format.ts"
import { dateAutoFill } from "../../../utils/form/date-auto-fill.tsx"
import { validateFormWithScroll } from "../../../utils/form/validate-form-with-scroll.tsx"
import { isFormChanged } from "../../../utils/object/is-form-changed.ts"
import {
  ATTACHMENT_MAX_SIZE_MB,
  stageAttachment
} from "../../../utils/attachment-upload.ts"
import { AttachmentModels } from "../../Album/types/attachment.types.ts"
import {
  deleteProjectAttachment,
  uploadProjectAttachment
} from "../../Project/redux/project-attachments/project-attachments.thunks.ts"
import {
  createInvoice,
  fetchInvoicesFilterOptions,
  patchInvoice
} from "../../Project/redux/project-invoices/project-invoices.thunks.ts"
import type { ProjectInvoice } from "../../Project/types/project-invoices.types.tsx"
import { mapAttachmentsToFileList } from "../../Project/ProjectViewPage/helpers/map-attachments-to-file-list.ts"
import { invoiceFormFieldErrors } from "../helpers/handle-invoice-api-error.ts"
import { fetchBillableLines } from "../redux/invoices.thunks.ts"
import {
  type BillableLine,
  INVOICE_STATUSES,
  type InvoiceLineInput
} from "../types/invoices.types.ts"
import { budgetFormat } from "../../../utils/number/budget-format.ts"
import {
  INVOICE_STATUSES_MAP,
  type InvoiceStatuses
} from "../../Project/ProjectViewPage/constants/invoice-statuses.ts"
import { ProjectViewInvoiceAssigneeSelect } from "../../Project/ProjectViewPage/components/sections/ProjectViewInvoices/ProjectViewInvoices/ProjectViewInvoiceAssigneeSelect.tsx"
import {
  buildInvoiceCreatePayload,
  buildInvoiceEditPatch,
  defaultLinkedLineAmount,
  isInvoiceAmountAbovePayment,
  linesToAmounts,
  linkedLinesExceedInvoice,
  linkedLinesTotal,
  partitionUploadResults
} from "../helpers/invoice-form.utils.ts"
import { ResolvedSelect } from "../../../components/Select/ResolvedSelect.tsx"
import { TextInput } from "../../../components/Input/TextInput.tsx"

type EditableInvoice = Omit<ProjectInvoice, "lines"> & {
  lines?: Array<{
    id?: string
    finance_line: string
    finance_line_name?: string
    amount: string
  }>
}

interface ExternalInvoiceDrawerProps {
  open: boolean
  onClose: () => void
  onSaved: (invoice: ProjectInvoice) => void
  selfAssigned?: boolean
  userId?: string
  paymentAmount?: number | null
  preselectedLineId?: string
  /** Locks the drawer to this project. Without it the route's project is used,
   * and outside a project the user picks one. */
  projectId?: string
  /** Opens the drawer on an existing external invoice. */
  invoice?: EditableInvoice
  /** Receivable or Payable preselected for a new invoice. */
  defaultType?: number
}

const EMPTY_INVOICE = {} as ProjectInvoice

const toFormValues = (invoice: EditableInvoice): Partial<ProjectInvoice> => ({
  invoice_number: invoice.invoice_number,
  date_issued: invoice.date_issued,
  due_date: invoice.due_date,
  amount: (invoice.amount != null
    ? Number(invoice.amount)
    : undefined) as unknown as string,
  type: invoice.type,
  assignee: invoice.assignee ?? null,
  customer: invoice.customer ?? null,
  service_type: invoice.service_type ?? null
})

export const ExternalInvoiceDrawer = ({
  open,
  onClose,
  onSaved,
  selfAssigned,
  userId,
  paymentAmount,
  preselectedLineId,
  projectId: projectIdProp,
  invoice,
  defaultType
}: ExternalInvoiceDrawerProps) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const routeProjectId = useParams()?.id as string | undefined
  const navigate = useNavigate()
  const { getDateValueProps, createDateRangeRules, normalizeDate } =
    useDateFormat()
  const { modal } = App.useApp()
  const screens = Grid.useBreakpoint()

  const isEditMode = Boolean(invoice)
  // An existing invoice keeps the project it has, none included: the backend
  // refuses to move it, so editing never asks for one.
  const lockedProjectId = isEditMode
    ? ((invoice?.project as string | null | undefined) ?? undefined)
    : (projectIdProp ?? routeProjectId)
  const [pickedProject, setPickedProject] = useState<string | undefined>()
  const [pickedLabel, setPickedLabel] = useState<string | undefined>()
  const projectId = lockedProjectId ?? pickedProject
  const needsProjectPicker = !isEditMode && !lockedProjectId

  const [form] = Form.useForm<ProjectInvoice>()
  const amountValue = Form.useWatch("amount", form)
  const invoiceExceedsPayment = isInvoiceAmountAbovePayment(
    amountValue,
    paymentAmount
  )
  const [saving, setSaving] = useState(false)
  const [options, setOptions] = useState<Record<string, BaseOptionType[]>>({})
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState<string[]>([])
  const [savedInvoice, setSavedInvoice] = useState<ProjectInvoice | null>(null)
  const [projectError, setProjectError] = useState(false)
  const baselineRef = useRef<ProjectInvoice>(EMPTY_INVOICE)

  const originalLines = useMemo(() => linesToAmounts(invoice?.lines), [invoice])
  const billableLines = useAppSelector((state) => state.invoices.billableLines)
  const [linkedLines, setLinkedLines] = useState<Record<string, number>>({})
  const linkedTotal = linkedLinesTotal(linkedLines)
  const linkedLinesExceed = linkedLinesExceedInvoice(
    linkedLines,
    amountValue != null ? Number(amountValue) : null
  )

  // A line this invoice already bills may have no balance left, so the
  // billable list drops it — it still has to be shown to be unlinked.
  const renderLines = useMemo<BillableLine[]>(() => {
    const known = new Set(billableLines.map((line) => line.id))
    const extra = (invoice?.lines ?? [])
      .filter((line) => !known.has(line.finance_line))
      .map((line) => ({
        id: line.finance_line,
        name: line.finance_line_name || empty,
        planned_value: line.amount,
        previously_billed: "0",
        balance_remaining: "0",
        entry_type: "",
        responsible_type: null
      }))
    return [...billableLines, ...extra]
  }, [billableLines, invoice])

  // The balance the server reports already excludes what this invoice bills,
  // so an edit may keep its own amount on top of it.
  const lineMax = (line: BillableLine) =>
    (Number(line.balance_remaining) || 0) + (originalLines[line.id] ?? 0)

  useEffect(() => {
    if (!open || !projectId) return
    void dispatch(fetchBillableLines(projectId))
    void dispatch(fetchInvoicesFilterOptions({ project_id: projectId }))
      .unwrap()
      .then(setOptions)
      .catch(() => undefined)
  }, [open, projectId, dispatch])

  useEffect(() => {
    if (!open || !preselectedLineId || isEditMode) return
    const line = billableLines.find((item) => item.id === preselectedLineId)
    if (!line) return
    setLinkedLines((prev) => {
      if (preselectedLineId in prev) return prev
      return {
        ...prev,
        [preselectedLineId]: defaultLinkedLineAmount(
          line.balance_remaining,
          paymentAmount != null && paymentAmount > 0 ? paymentAmount : null,
          0
        )
      }
    })
  }, [open, preselectedLineId, billableLines, paymentAmount, isEditMode])

  useEffect(() => {
    if (!open) return

    form.resetFields()
    setSavedInvoice(null)
    setRemovedAttachmentIds([])
    setProjectError(false)
    setPickedProject(undefined)
    setPickedLabel(undefined)

    if (invoice) {
      form.setFieldsValue(toFormValues(invoice))
      setLinkedLines(linesToAmounts(invoice.lines))
      setFileList(mapAttachmentsToFileList(invoice.attachments ?? []))
    } else {
      setFileList([])
      setLinkedLines({})
      if (defaultType != null) form.setFieldValue("type", defaultType)
      if (selfAssigned && userId) form.setFieldValue("assignee", userId)
      if (paymentAmount != null && paymentAmount > 0) {
        form.setFieldValue("amount", paymentAmount)
      }
    }
    baselineRef.current = form.getFieldsValue()
  }, [open, invoice, form, selfAssigned, userId, paymentAmount, defaultType])

  const stageFile = stageAttachment(setFileList)

  const isExistingAttachment = (file: UploadFile) =>
    Boolean(invoice?.attachments?.some((item) => item.id === file.uid))

  const removeFile = (target: UploadFile) => {
    if (isExistingAttachment(target)) {
      setRemovedAttachmentIds((prev) => [...prev, target.uid])
      setFileList((prev) => prev.filter((f) => f.uid !== target.uid))
      return false
    }
    if (target.status === "done") return false
    setFileList((prev) => prev.filter((f) => f.uid !== target.uid))
    return false
  }

  const uploadStagedFiles = async (target: ProjectInvoice) => {
    const pending = fileList.filter(
      (f) => f.status !== "done" && f.originFileObj
    )

    if (pending.length === 0) return { failedNames: [] as string[] }

    const pendingUids = new Set(pending.map((f) => f.uid))
    setFileList((prev) =>
      prev.map((f) =>
        pendingUids.has(f.uid) ? { ...f, status: "uploading" } : f
      )
    )

    const results = await Promise.allSettled(
      pending.map((f) =>
        dispatch(
          uploadProjectAttachment({
            data: {
              model_type: AttachmentModels.INVOICE,
              object_id: target.id,
              project_id: projectId as string,
              file: f.originFileObj as RcFile
            },
            silent: true
          })
        ).unwrap()
      )
    )

    const { doneUids, failedNames } = partitionUploadResults(pending, results)
    const doneSet = new Set(doneUids)
    setFileList((prev) =>
      prev.map((f) =>
        pendingUids.has(f.uid)
          ? { ...f, status: doneSet.has(f.uid) ? "done" : "error" }
          : f
      )
    )

    return { failedNames }
  }

  const removeDeletedAttachments = async (target: ProjectInvoice) => {
    const ids = removedAttachmentIds
    if (!ids.length) return
    await Promise.allSettled(
      ids.map((attachmentId) =>
        dispatch(
          deleteProjectAttachment({
            attachment_id: attachmentId,
            object_id: target.id,
            model_type: AttachmentModels.INVOICE
          })
        ).unwrap()
      )
    )
    setRemovedAttachmentIds([])
  }

  const showFieldErrors = (error: unknown) => {
    const fieldErrors = invoiceFormFieldErrors(error)
    if (!fieldErrors.length) return
    form.setFields(
      fieldErrors.map(({ name, errors }) => ({
        name: name as keyof ProjectInvoice,
        errors
      }))
    )
    form.scrollToField(fieldErrors[0].name, { block: "center" })
  }

  const saveRecord = async (): Promise<ProjectInvoice | null> => {
    let values: ProjectInvoice
    try {
      values = await validateFormWithScroll(form)
    } catch {
      if (needsProjectPicker && !projectId) setProjectError(true)
      return null
    }
    if (needsProjectPicker && !projectId) {
      setProjectError(true)
      return null
    }
    if (linkedLinesExceed) return null

    try {
      setSaving(true)
      if (invoice) {
        const patch = buildInvoiceEditPatch(
          values,
          toFormValues(invoice),
          normalizeDate,
          linkedLines,
          originalLines
        )
        if (!Object.keys(patch).length) return invoice
        return await dispatch(
          patchInvoice({
            id: invoice.id,
            ...patch,
            silent: true,
            suppressError: true
          })
        ).unwrap()
      }

      const payload = buildInvoiceCreatePayload(
        values,
        projectId as string,
        normalizeDate
      )
      const lines: InvoiceLineInput[] = Object.entries(linkedLines).map(
        ([financeLine, amount]) => ({
          finance_line: financeLine,
          amount: String(amount)
        })
      )
      return await dispatch(
        createInvoice({
          ...payload,
          status: INVOICE_STATUSES.ISSUED,
          ...(lines.length ? { lines } : {}),
          silent: fileList.length > 0 || needsProjectPicker
        })
      ).unwrap()
    } catch (error) {
      setSaving(false)
      showFieldErrors(error)
      return null
    }
  }

  const handleSave = async () => {
    let target = savedInvoice

    if (!target) {
      target = await saveRecord()
      if (!target) return
      setSavedInvoice(target)
    } else {
      setSaving(true)
    }

    const attachedCount = fileList.filter(
      (f) => f.status !== "done" && f.originFileObj
    ).length
    await removeDeletedAttachments(target)
    const { failedNames } = await uploadStagedFiles(target)
    setSaving(false)

    if (failedNames.length > 0) {
      notification.warning({
        ...notificationOptions.warning,
        message: t("finance.quickAddInvoice.attachments.partialError", {
          count: failedNames.length
        }),
        description: t("finance.quickAddInvoice.attachments.partialErrorHint")
      })
      return
    }

    if (!isEditMode && needsProjectPicker) {
      // Outside a project the invoice lands somewhere the user is not looking,
      // so the toast names it and offers the way there.
      notification.success({
        ...notificationOptions.success,
        message: t("invoices.externalDrawer.created"),
        description: (
          <span data-testid="invoice-saved-in-project">
            {t("invoices.savedInProject", { project: pickedLabel || empty })} ·{" "}
            <Button
              type="link"
              size="small"
              className="p-0"
              onClick={() =>
                navigate(RoutesPaths.project.view(projectId as string).invoices)
              }
            >
              {t("invoices.viewProject")}
            </Button>
          </span>
        )
      })
    } else if (isEditMode) {
      notification.success({
        ...notificationOptions.success,
        message: t("invoices.externalDrawer.updated")
      })
    } else if (attachedCount > 0) {
      notification.success({
        ...notificationOptions.success,
        message: t(
          "finance.quickAddInvoice.attachments.createdWithAttachments",
          {
            count: attachedCount
          }
        )
      })
    }

    onSaved(target)
  }

  const handleClose = () => {
    if (saving) return

    if (savedInvoice) {
      onSaved(savedInvoice)
      return
    }

    const changed =
      Boolean(pickedProject) ||
      removedAttachmentIds.length > 0 ||
      fileList.some((f) => f.status !== "done") ||
      JSON.stringify(linkedLines) !== JSON.stringify(originalLines) ||
      isFormChanged<ProjectInvoice>(form.getFieldsValue(), baselineRef.current)

    if (changed) {
      modal.confirm({ ...MODAL_CONFIRM_CONFIG.cancelChanges, onOk: onClose })
    } else {
      onClose()
    }
  }

  const statusShown = (
    isEditMode ? invoice?.status : INVOICE_STATUSES.ISSUED
  ) as InvoiceStatuses | undefined
  const statusDisplay = statusShown
    ? INVOICE_STATUSES_MAP[statusShown]
    : undefined

  return (
    <Drawer
      title={t(
        isEditMode
          ? "invoices.externalDrawer.editTitle"
          : "invoices.externalDrawer.title"
      )}
      placement="right"
      width={screens.md ? 520 : "100%"}
      open={open}
      onClose={handleClose}
      closable={!saving}
      maskClosable={!saving}
      keyboard={!saving}
      footer={
        <Flex justify="space-between" gap="small">
          <Button onClick={handleClose} disabled={saving}>
            {savedInvoice
              ? t("finance.quickAddInvoice.attachments.close")
              : t("finance.quickAddInvoice.cancel")}
          </Button>
          <Button
            type="primary"
            loading={saving}
            disabled={!savedInvoice && linkedLinesExceed}
            onClick={handleSave}
            data-testid="quick-add-invoice-submit"
          >
            {savedInvoice
              ? t("finance.quickAddInvoice.attachments.retry")
              : isEditMode
                ? t("invoices.externalDrawer.saveChanges")
                : t("finance.quickAddInvoice.create")}
          </Button>
        </Flex>
      }
    >
      <Form<ProjectInvoice>
        form={form}
        layout="vertical"
        // Required: Form.less hides .ant-form-item-explain without this class, so
        // validation messages would render as a coloured border only, no text.
        className="with-default-error"
        disabled={saving || !!savedInvoice}
      >
        <Typography.Title level={5}>
          {t("finance.quickAddInvoice.sections.details")}
        </Typography.Title>

        {needsProjectPicker && (
          <Form.Item
            label={t("invoices.drawer.fields.project")}
            required
            validateStatus={projectError ? "error" : undefined}
            help={
              projectError
                ? t("finance.quickAddInvoice.validation.required")
                : undefined
            }
          >
            <ProjectPickerSelect
              data-testid="external-invoice-project"
              value={pickedProject}
              disabled={saving}
              onChange={(value, option) => {
                setPickedProject((value as string) ?? undefined)
                setPickedLabel(
                  (option as { label?: string } | undefined)?.label ?? undefined
                )
                setProjectError(false)
                setLinkedLines({})
              }}
            />
          </Form.Item>
        )}

        <Form.Item<ProjectInvoice>
          name="invoice_number"
          label={t("finance.quickAddInvoice.fields.invoiceNumber")}
          rules={[
            {
              required: true,
              message: t("finance.quickAddInvoice.validation.required")
            },
            {
              max: 50,
              message: t("finance.quickAddInvoice.validation.invoiceNumberMax")
            }
          ]}
        >
          <TextInput
            disableAutoCapitalize
            data-testid="quick-add-invoice-number"
            placeholder={t(
              "finance.quickAddInvoice.placeholders.invoiceNumber"
            )}
            autoFocus
          />
        </Form.Item>

        <Flex gap="middle">
          <Form.Item<ProjectInvoice>
            className="flex-1"
            name="date_issued"
            label={t("finance.quickAddInvoice.fields.dateIssued")}
            getValueProps={getDateValueProps}
            dependencies={["due_date"]}
            rules={[
              {
                required: true,
                message: t("finance.quickAddInvoice.validation.required")
              },
              ...createDateRangeRules<ProjectInvoice>("due_date", "start")
            ]}
          >
            <DatePicker
              className="w-full"
              onChange={dateAutoFill<ProjectInvoice>({
                form,
                endDatePath: ["due_date"]
              })}
            />
          </Form.Item>

          <Form.Item<ProjectInvoice>
            className="flex-1"
            name="due_date"
            label={t("finance.quickAddInvoice.fields.dueDate")}
            getValueProps={getDateValueProps}
            dependencies={["date_issued"]}
            rules={[
              {
                required: true,
                message: t("finance.quickAddInvoice.validation.required")
              },
              ...createDateRangeRules<ProjectInvoice>("date_issued", "end")
            ]}
          >
            <DatePicker className="w-full" />
          </Form.Item>
        </Flex>

        <Form.Item<ProjectInvoice>
          name="amount"
          label={t("finance.quickAddInvoice.fields.amount")}
          rules={[
            {
              required: true,
              message: t("finance.quickAddInvoice.validation.required")
            }
          ]}
          // Advisory only: an invoice above the payment it covers is allowed, so
          // warn without blocking the save.
          validateStatus={invoiceExceedsPayment ? "warning" : undefined}
          help={
            invoiceExceedsPayment
              ? t("finance.quickAddInvoice.validation.amountExceedsPayment", {
                  amount: `$${paymentAmount}`
                })
              : undefined
          }
        >
          <InputNumber
            min={0}
            className="w-full"
            prefix="$"
            placeholder="0.00"
          />
        </Form.Item>

        <Typography.Title level={5}>
          {t("finance.quickAddInvoice.sections.classification")}
        </Typography.Title>

        <Form.Item<ProjectInvoice>
          name="type"
          label={t("finance.quickAddInvoice.fields.type")}
          rules={[
            {
              required: true,
              message: t("finance.quickAddInvoice.validation.required")
            }
          ]}
        >
          <ResolvedSelect
            placeholder={t("finance.quickAddInvoice.placeholders.select")}
            options={options?.["type"]}
          />
        </Form.Item>

        <Form.Item label={t("finance.quickAddInvoice.fields.status")}>
          <Tag style={statusDisplay?.style} className="m-0">
            {isEditMode
              ? invoice?.status_display || statusDisplay?.label || empty
              : t("finance.quickAddInvoice.issuedStatus")}
          </Tag>
        </Form.Item>

        <Form.Item label={t("finance.quickAddInvoice.fields.assignee")}>
          <ProjectViewInvoiceAssigneeSelect
            form={form}
            disabled={selfAssigned || undefined}
            options={{
              subcontractors: options?.["subcontractors"] as
                | { label: string; value: string | number }[]
                | undefined,
              customers: options?.["customers"] as
                | { label: string; value: string | number }[]
                | undefined
            }}
          />
        </Form.Item>

        <Form.Item<ProjectInvoice>
          name="service_type"
          label={t("finance.quickAddInvoice.fields.serviceType")}
          normalize={(v) => v || null}
        >
          <ResolvedSelect
            allowClear
            placeholder={t("finance.quickAddInvoice.placeholders.select")}
            options={options?.["service_type"]}
          />
        </Form.Item>

        {projectId && renderLines.length > 0 && (
          <>
            <Typography.Title level={5}>
              {t("invoices.linkLines.title")}
            </Typography.Title>
            <Typography.Text type="secondary" className="text-xs">
              {t("invoices.linkLines.hint")}
            </Typography.Text>
            <Flex vertical gap="small" className="mt-2 mb-4">
              {renderLines.map((line) => {
                const checked = line.id in linkedLines
                return (
                  <Flex key={line.id} vertical gap={2}>
                    <Flex align="center" justify="space-between" gap="small">
                      <Checkbox
                        checked={checked}
                        disabled={saving || !!savedInvoice}
                        onChange={(event) =>
                          setLinkedLines((prev) => {
                            const next = { ...prev }
                            if (event.target.checked) {
                              next[line.id] = defaultLinkedLineAmount(
                                lineMax(line),
                                amountValue != null
                                  ? Number(amountValue)
                                  : null,
                                linkedLinesTotal(prev)
                              )
                            } else {
                              delete next[line.id]
                            }
                            return next
                          })
                        }
                      >
                        <Typography.Text>{line.name}</Typography.Text>
                      </Checkbox>
                      <HintedLabel
                        hint={t("invoices.drawer.balanceHint")}
                        testId={`quick-add-hint-balance-${line.id}`}
                        field={t("invoices.drawer.labels.balance")}
                      >
                        <Typography.Text type="secondary" className="text-xs">
                          {t("invoices.drawer.balance", {
                            balance: budgetFormat(line.balance_remaining)
                          })}
                        </Typography.Text>
                      </HintedLabel>
                    </Flex>
                    {checked && (
                      <InputNumber
                        min={0.01}
                        max={lineMax(line)}
                        prefix="$"
                        className="w-full"
                        value={linkedLines[line.id]}
                        data-testid={`quick-add-line-amount-${line.id}`}
                        placeholder={t("invoices.linkLines.amountOptional")}
                        onChange={(value) =>
                          setLinkedLines((prev) => ({
                            ...prev,
                            [line.id]: (value as number) ?? 0
                          }))
                        }
                      />
                    )}
                  </Flex>
                )
              })}

              {Object.keys(linkedLines).length > 0 && (
                <Flex align="center" justify="space-between" gap="small">
                  <Typography.Text
                    type={linkedLinesExceed ? "danger" : "secondary"}
                    className="text-xs"
                  >
                    {t("invoices.linkLines.linkedTotal")}
                  </Typography.Text>
                  <Typography.Text
                    type={linkedLinesExceed ? "danger" : undefined}
                    strong
                  >
                    {budgetFormat(String(linkedTotal))}
                  </Typography.Text>
                </Flex>
              )}

              {linkedLinesExceed && (
                <Typography.Text type="danger" className="text-xs">
                  {t("invoices.linkLines.exceedsInvoiceAmount")}
                </Typography.Text>
              )}
            </Flex>
          </>
        )}

        <Typography.Title level={5}>
          {t("finance.quickAddInvoice.sections.attachments")}
        </Typography.Title>

        <Flex vertical gap="small">
          <Upload
            multiple
            listType="text"
            fileList={fileList}
            beforeUpload={stageFile}
            onRemove={removeFile}
            disabled={saving || !projectId}
            showUploadList={{
              showRemoveIcon: (file) =>
                !saving &&
                !savedInvoice &&
                (file.status !== "done" || isExistingAttachment(file))
            }}
          >
            <Button icon={<UploadOutlined />} disabled={saving || !projectId}>
              {t("finance.quickAddInvoice.attachments.add")}
            </Button>
          </Upload>
          <Typography.Text type="secondary" className="text-xs">
            {t("files.upload.maxSize", { size: ATTACHMENT_MAX_SIZE_MB })}
          </Typography.Text>
        </Flex>
      </Form>
    </Drawer>
  )
}
